/**
 * Buying credits, in a browser.
 *
 * Stripe Checkout, never RevenueCat: react-native-purchases is a native module and an IAP
 * button in a browser is a dead end. The server owns the money — POST /billing/checkout
 * mints a session bound to the caller's Cognito sub, and the signed webhook is what
 * actually grants credits. This only asks for the session and hands the browser over.
 */
import { ApiError, request } from './client';

export type CheckoutResult =
  | { status: 'redirecting'; url: string }
  | { status: 'signin_required' }
  | { status: 'error'; message: string };

export async function startCheckout(
  redirect: (url: string) => void = (url) => window.location.assign(url),
): Promise<CheckoutResult> {
  try {
    const { url } = await request<{ url?: string }>('/billing/checkout', { method: 'POST' });
    // Navigating to undefined would land someone on a blank page mid-purchase.
    if (!url) return { status: 'error', message: 'Could not start checkout.' };
    redirect(url);
    return { status: 'redirecting', url };
  } catch (err) {
    if (err instanceof ApiError) {
      // A dead button is worse than a prompt they can act on.
      if (err.kind === 'unauthorized') return { status: 'signin_required' };
      return { status: 'error', message: err.message };
    }
    return { status: 'error', message: 'Could not start checkout.' };
  }
}

/**
 * The balance, and whether the free first build is still there.
 *
 * `firstBuildDone` has always been in this response and this client dropped it on the floor
 * — the same shape as the phone's bug, where the app could not say "this one is free"
 * because it had never read the field that says so. Optional in the type because an older
 * server would not send it, and absent must read as "unknown" rather than as "spent".
 */
export async function creditBalance(): Promise<{
  creditsRemaining: number;
  tier: string;
  firstBuildDone?: boolean;
}> {
  return request<{ creditsRemaining: number; tier: string; firstBuildDone?: boolean }>('/credits');
}

export type PortalResult =
  | { status: 'redirecting'; url: string }
  | { status: 'signin_required' }
  /** No Stripe customer at all — they subscribed through the App Store. A normal answer. */
  | { status: 'managed_elsewhere' }
  | { status: 'error'; message: string };

/**
 * The door out. Opens Stripe's billing portal so a subscriber can actually cancel.
 *
 * This closes a promise the product was already making: strings.ts has been telling web
 * customers they can "cancel anytime from your account" with nothing behind it. A
 * subscription you cannot leave from where you were told you could leave it is the kind of
 * thing that is both a support burden and, in several places, a legal one.
 *
 * Three answers that are not errors, and one that is:
 *  - 404 `no_stripe_subscription` means this person pays through the App Store. That is a
 *    FACT about them, not a failure, so they are told where to go rather than shown a
 *    problem to solve.
 *  - 502 carries Stripe's own sentence, which is the useful part: the common one is "No
 *    configuration provided", meaning the portal is not enabled in the Stripe dashboard.
 *    Replacing that with "the server had a problem" would hide the one instruction anybody
 *    could act on, so it is surfaced verbatim.
 *  - 401 asks them to sign in, rather than presenting a dead button.
 *
 * Someone who subscribed before the customer id was stored is found by an email lookup on
 * the first visit and remembered after that, so the FIRST call for such a person is slow.
 * That is why this gets a longer deadline than an ordinary data call.
 */
const PORTAL_TIMEOUT_MS = 30_000;

export async function openBillingPortal(
  redirect: (url: string) => void = (url) => window.location.assign(url),
): Promise<PortalResult> {
  try {
    const { url } = await request<{ url?: string }>('/billing/portal', {
      method: 'POST',
      timeoutMs: PORTAL_TIMEOUT_MS,
    });
    if (!url) return { status: 'error', message: 'Could not open the billing portal.' };
    redirect(url);
    return { status: 'redirecting', url };
  } catch (err) {
    if (!(err instanceof ApiError)) return { status: 'error', message: 'Could not open the billing portal.' };
    if (err.kind === 'unauthorized') return { status: 'signin_required' };
    /*
     * Read off `reason`, not `code`: this route puts its machine token in the body's
     * `error` field, which client.ts warns is used BOTH ways across the API. Branching on
     * `code` here would never fire, and everyone would be told to check their connection.
     */
    if (err.kind === 'not_found' && err.reason === 'no_stripe_subscription') {
      return { status: 'managed_elsewhere' };
    }
    // Stripe's own words for a gateway failure; ours for anything else.
    if (err.status === 502 && err.reason) return { status: 'error', message: err.reason };
    return { status: 'error', message: err.message };
  }
}

export type RedeemResult =
  /**
   * `creditsRemaining` is the balance AFTER the code, when the server says it. Absent is left
   * absent rather than read as 0: a screen showing "0" to someone who just added credits is the
   * bug the Credits screen's "—" exists to prevent.
   */
  | { status: 'redeemed'; creditsRemaining?: number; granted: number; tier?: string }
  /**
   * `reason` is the server's own token ('invalid_code', 'already_redeemed', 'code_expired',
   * 'code_exhausted', 'promo_unavailable', 'signup_required') or 'rate_limited', or the client's
   * error kind for anything else. The screen turns it into words with the phone's own
   * `redeemFailureMessage`, which answers an unknown one with the generic sentence.
   */
  | { status: 'refused'; reason: string };

/**
 * Redeem a promo code: `POST /redeem` with `{ code }`, the call the phone makes (guidedClient.ts).
 *
 * On the LLM Function URL, not the API gateway: /redeem is a route of the LLM proxy and the
 * gateway has no resource for it, so `base: 'api'` would answer 403/404 for every code. The phone
 * reaches it the same way, through EXPO_PUBLIC_LLM_URL.
 *
 * Sent once and never retried (the client only repeats a GET), though the server would refuse a
 * second redemption anyway: it claims a per-account marker before it grants anything.
 */
export async function redeemCode(code: string): Promise<RedeemResult> {
  try {
    const data = await request<{ creditsRemaining?: number; granted?: number; tier?: string }>('/redeem', {
      base: 'llm',
      method: 'POST',
      body: { code: code.trim() },
    });
    return {
      status: 'redeemed',
      granted: data?.granted ?? 0,
      ...(typeof data?.creditsRemaining === 'number' ? { creditsRemaining: data.creditsRemaining } : {}),
      ...(data?.tier ? { tier: data.tier } : {}),
    };
  } catch (err) {
    if (!(err instanceof ApiError)) return { status: 'refused', reason: 'unknown' };
    /*
     * The status decides these two, whatever the body says: a 401 from this route is always
     * "no account", and a 429 may come from Lambda's own throttle, whose body is not ours.
     */
    if (err.kind === 'unauthorized') return { status: 'refused', reason: 'signup_required' };
    if (err.kind === 'rate_limited') return { status: 'refused', reason: 'rate_limited' };
    // The machine token is in the body's `error` field on this route, which the client keeps
    // as `reason` (see client.ts on why that field is read per route).
    return { status: 'refused', reason: err.reason ?? err.kind };
  }
}
